import getLogger from '@ignitesales/logger-es';
import React from 'react';
import ignite from './ignite.mjs';
import InitScreen from './InitScreen.jsx';
import { getStore } from './stores/main-store.mjs';
import NeuroRoot from './NeuroRoot.jsx';
import ReactDOM from 'react-dom';
import * as defaultQuestionTypes from './components/questions/types/index.mjs';
import * as defaultQuestionLayouts from './components/questions/containers/index.mjs';
import { setMessages } from './i18n/messageUtil.mjs';
import areIntlLocalesSupported from 'intl-locales-supported';

const logger = getLogger({ component: 'NeuroApplication' });

let isInitialized = false;

/**
 * @typedef NeuroInitOptions
 * @prop {React.Component} AppComponent
 * @prop {HTMLElement} htmlElement
 * @prop {boolean} [isBranchGuide]
 * @prop {React.Component} LoadingComponent
 * @prop {string} [locale]
 * @prop {Object} messages
 * @prop {Array} [middlewares]
 * @prop {Object} [questionLayouts]
 * @prop {Object} [questionTypes]
 * @prop {Array} [reducers]
 * @prop {Array} [supportedLocales]
 * @prop {Function} decorateStateFn
 *
 * @type NeuroInitOptions
 */
const defaultOpts = {
  AppComponent: null,
  htmlElement: null,
  isBranchGuide: false,
  LoadingComponent: null,
  locale: 'en',
  messages: null,
  middlewares: [],
  questionLayouts: defaultQuestionLayouts,
  questionTypes: defaultQuestionTypes,
  reducers: [],
  supportedLocales: [ 'en' ],
  decorateStateFn: null
};

/**
 * Neuro application initialization function
 *
 * @param {NeuroInitOptions} opts
 * @returns {Promise<boolean>}
 */
const initializeNeuroApplication = async (opts) => {
  logger.info('Initializing Ignite Neuro application');

  const _opts = {
    ...defaultOpts,
    ...opts
  };

  const htmlElement = (_opts.htmlElement) ? _opts.htmlElement : document.getElementById('root');
  const {
    AppComponent,
    isBranchGuide,
    LoadingComponent,
    locale,
    messages,
    middlewares,
    questionLayouts,
    questionTypes,
    reducers,
    supportedLocales,
    decorateStateFn
  } = _opts;

  setMessages(messages);

  // show loading screen
  const initScreenProps = {
    LoadingComponent,
    locale,
    messages
  };
  const initScreen = React.createElement(InitScreen, initScreenProps, null);
  ReactDOM.render(
    initScreen,
    htmlElement
  );

  // @todo we should be able to determine this statically and use tree-shaking to eliminate need for dynamic imports
  if (!areIntlLocalesSupported(supportedLocales)) {
    const childLog = logger.child({ supportedLocales });
    childLog.info('importing locale data');
    const promises = [
      import('intl-pluralrules'),
      import('@formatjs/intl-relativetimeformat/polyfill')
    ];
    supportedLocales.forEach(locale => {
      promises.push(import('@formatjs/intl-relativetimeformat/dist/locale-data/' + locale));
    });

    await Promise.all(promises);

    childLog.info('Completed importing locale data');
  }

  // initialize main store
  const mainStore = await getStore({
    reducers,
    middlewares,
    loadInitialState: ignite.loadInitialState,
    isBranchGuide,
    decorateStateFn
  });

  // eslint-disable-next-line require-atomic-updates
  ignite.reduxStore = mainStore;

  // render neuro application root to DOM
  const rootProps = {
    AppComponent,
    locale,
    mainStore,
    messages,
    questionLayouts: {...defaultOpts.questionLayouts, ...questionLayouts},
    questionTypes: {...defaultOpts.questionTypes, ...questionTypes},
  };

  ignite.reduxStore.dispatch({
    type: 'APP_READY'
  });

  const renderP = new Promise((resolve, reject) => {
    const neuroRoot = React.createElement(NeuroRoot, rootProps, null);

    const renderCb = () => {
      resolve(true);
    };

    try {
      ReactDOM.render(neuroRoot, htmlElement, renderCb);
    } catch (err) {
      reject(err);
    }
  });

  isInitialized = await renderP
    .catch( (err) => {
      throw new Error('neuro initialization failed', err);
    });

  logger.info('neuro initialization complete');
};

export {
  initializeNeuroApplication as default,
  initializeNeuroApplication,
  isInitialized
};
